import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { Lock, CheckCircle, AlertCircle, Copy, ExternalLink, Download, Printer } from 'lucide-react';
import { toast } from 'react-toastify';

let jsPDFLoader = null;

const ensureJsPDF = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('PDF generation is not supported in this environment.'));
  }

  if (window.jspdf?.jsPDF) {
    return Promise.resolve(window.jspdf.jsPDF);
  }

  if (jsPDFLoader) {
    return jsPDFLoader;
  }

  const existingScript = document.getElementById('jspdf-cdn');

  jsPDFLoader = new Promise((resolve, reject) => {
    const cleanup = () => {
      jsPDFLoader = null;
    };

    const handleLoad = () => {
      if (window.jspdf?.jsPDF) {
        resolve(window.jspdf.jsPDF);
      } else {
        cleanup();
        reject(new Error('PDF library loaded but jsPDF is not available.'));
      }
    };

    const handleError = () => {
      cleanup();
      reject(new Error('Failed to load PDF library.'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'jspdf-cdn';
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.referrerPolicy = 'no-referrer';
    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);
  });

  return jsPDFLoader;
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Content = styled.div`
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 2rem;
`;

const ApplicationCard = styled(Card)`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Instructions = styled.div`
  background: ${props => props.theme.colors.primary}10;
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: 1rem;
  margin-bottom: 2rem;
  border-radius: 8px;
`;

const ClosedMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
`;

const ClosedIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.theme.colors.error}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme.colors.error};
`;

const TrackingBox = styled.div`
  border: 1px dashed ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary}05;
  padding: 1.5rem;
  border-radius: 12px;
  margin-top: 1.5rem;
`;

const TrackingNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: ${props => props.theme.colors.primary};
`;

const TrackingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  justify-content: center;
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const FieldHelp = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const FieldError = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.error};
  font-weight: 500;
`;

const DISABILITY_OPTIONS = [
  'Mobility impairment',
  'Visual impairment',
  'Hearing impairment',
  'Speech impairment',
  'Intellectual or learning disability',
  'Psychosocial disability',
  'Chronic illness',
  'Other (please specify)',
];

const OTHER_DISABILITY_OPTION = 'Other (please specify)';
const ACCESSIBILITY_FIELDS = ['has_disability', 'disability_types', 'disability_other', 'cv'];

const ETHIOPIAN_REGIONS = [
  'Addis Ababa',
  'Afar',
  'Amhara',
  'Benishangul-Gumuz',
  'Central Ethiopia',
  'Dire Dawa',
  'Gambela',
  'Harari',
  'Oromia',
  'Sidama',
  'Somali',
  'South West Ethiopia',
  'Southern Nations, Nationalities, and Peoples',
  'Tigray',
];

const REGION_FIELD_NAMES = new Set([
  'region',
  'region_city',
  'region_city_town',
  'region_city_zone',
  'region_city_woreda',
]);

const orderedFieldNames = [
  'full_name',
  'gender',
  'age',
  'phone',
  'email',
  'occupation_position',
  'region',
  'sector',
  'sector_other',
  'region_city',
  'interest_in_ewhrdn',
  'womens_rights_issues',
  'challenges_gender_equality',
  'short_biography',
  'political_affiliation_declaration',
  'signature_full_name',
  'email_confirmation',
];

const SectionDivider = styled.div`
  margin: 2rem 0 1.5rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const SectionHeading = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.border.light};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary + '10' : theme.colors.background.secondary};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}14;
  }
`;

const FileNote = styled.span`
  display: block;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HelperText = styled.p`
  margin: 0.75rem 0 0 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PublicMembershipApplication = () => {
  const { theme } = useTheme();
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const cvInputRef = useRef(null);
  const idCardInputRef = useRef(null);
  const trainingCertInputRef = useRef(null);

  const isAdminPreview = typeof window !== 'undefined' && window.location.pathname.startsWith('/app/');

  const [fieldErrors, setFieldErrors] = useState({});

  const disabilityStatus = formData.has_disability || 'no';
  const disabilitySelections = Array.isArray(formData.disability_types)
    ? formData.disability_types
    : [];
  const disabilityOther = formData.disability_other || '';
  const cvFile = formData.cv instanceof File ? formData.cv : null;

  const disabilityError = fieldErrors.disability_types || fieldErrors.has_disability;
  const disabilityOtherError = fieldErrors.disability_other;
  const cvError = fieldErrors.cv;

  useEffect(() => {
    fetchForm();
  }, [slug]);

  const fetchForm = async () => {
    try {
      const res = await membershipApplicationAPI.getFormBySlug(slug);
      const formData = res.data;
      const activeFields = formData.activeFields || formData.active_fields || [];

      setForm({
        ...formData,
        activeFields,
      });

      const initialData = {};
      activeFields.forEach(field => {
        if (field.field_type === 'checkbox') {
          initialData[field.field_name] = [];
        } else if (field.field_type === 'file') {
          initialData[field.field_name] = null;
        } else {
          initialData[field.field_name] = '';
        }
      });
      if (!('has_disability' in initialData)) {
        initialData.has_disability = 'no';
      }
      if (!('disability_types' in initialData)) {
        initialData.disability_types = [];
      }
      if (!('disability_other' in initialData)) {
        initialData.disability_other = '';
      }
      initialData.cv = null;
      
      // New fields for direct membership
      initialData.id_card = null;
      initialData.interested_trainings = []; // Multiple selection
      initialData.has_previous_training = 'no';
      initialData.previous_trainings = []; // Array of training objects
      
      setFormData(initialData);
      setFieldErrors({});
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load application form');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleFileChange = (fieldName, file) => {
    if (isAdminPreview) return;
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleDisabilityStatusChange = (value) => {
    if (isAdminPreview) return;
    setFormData(prev => ({
      ...prev,
      has_disability: value,
      disability_types: value === 'yes' ? (prev.disability_types || []) : [],
      disability_other: value === 'yes' ? prev.disability_other || '' : '',
    }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next.has_disability;
      delete next.disability_types;
      delete next.disability_other;
      return next;
    });
  };

  const toggleDisabilityType = (option) => {
    if (isAdminPreview) return;
    const currentSelections = Array.isArray(disabilitySelections) ? disabilitySelections : [];
    const exists = currentSelections.includes(option);
    const nextTypes = exists
      ? currentSelections.filter(item => item !== option)
      : [...currentSelections, option];

    setFormData(prev => ({
      ...prev,
      disability_types: nextTypes,
      disability_other: nextTypes.includes(OTHER_DISABILITY_OPTION)
        ? prev.disability_other || ''
        : '',
    }));

    setFieldErrors(prev => {
      const next = { ...prev };
      delete next.disability_types;
      if (!nextTypes.includes(OTHER_DISABILITY_OPTION)) {
        delete next.disability_other;
      }
      return next;
    });
  };

  const handleDisabilityOtherChange = (value) => {
    if (isAdminPreview) return;
    setFormData(prev => ({
      ...prev,
      disability_other: value,
    }));
    setFieldErrors(prev => {
      if (!prev.disability_other) return prev;
      const next = { ...prev };
      delete next.disability_other;
      return next;
    });
  };

  const handleRemoveCv = () => {
    if (isAdminPreview) return;
    setFormData(prev => ({
      ...prev,
      cv: null,
    }));
    if (cvInputRef.current) {
      cvInputRef.current.value = '';
    }
    setFieldErrors(prev => {
      if (!prev.cv) return prev;
      const next = { ...prev };
      delete next.cv;
      return next;
    });
  };

  const handleDownloadTrackingPdf = async () => {
    if (!submissionResult?.registration_no) {
      toast.error('Tracking number not available.');
      return;
    }

    let jsPDF;
    try {
      jsPDF = await ensureJsPDF();
    } catch (error) {
      console.error('Failed to load jsPDF library', error);
      toast.error('Unable to generate PDF right now. Please try again later.');
      return;
    }

    try {
      const doc = new jsPDF();
      const leftMargin = 15;
      let currentY = 20;

      doc.setFontSize(18);
      doc.text('Ethiopia WHRDN Membership Application', leftMargin, currentY);

      currentY += 12;
      doc.setFontSize(12);
      doc.text(`Generated: ${new Date().toLocaleString()}`, leftMargin, currentY);

      currentY += 20;
      doc.setFontSize(14);
      doc.text('Registration Number', leftMargin, currentY);

      currentY += 12;
      doc.setDrawColor(30, 64, 175);
      doc.setLineWidth(0.5);
      doc.rect(leftMargin, currentY - 10, 180, 14);
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(20);
      doc.text(submissionResult.registration_no, leftMargin + 5, currentY);

      currentY += 20;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      const instructions = 'Please keep this number safe. You will need it to check your application status or reference your submission.';
      const lines = doc.splitTextToSize(instructions, 180);
      doc.text(lines, leftMargin, currentY);

      doc.save(`registration-${submissionResult.registration_no}.pdf`);
      toast.success('Registration number downloaded as PDF');
    } catch (error) {
      console.error('Failed to generate PDF', error);
      toast.error('Unable to generate the PDF. Please try again.');
    }
  };

  const activeFields = useMemo(() => {
    return form?.activeFields || form?.active_fields || [];
  }, [form]);

  const renderDynamicField = (field, keyOverride) => (
    <FormField key={keyOverride || field.id || field.field_name}>
      <FieldLabel theme={theme}>
        {field.field_label}
        {field.is_required && <span className="required"> *</span>}
      </FieldLabel>
      {renderField(field)}
      {field.help_text && (
        <FieldHelp theme={theme}>{field.help_text}</FieldHelp>
      )}
      {fieldErrors[field.field_name] && (
        <FieldError theme={theme}>{fieldErrors[field.field_name]}</FieldError>
      )}
    </FormField>
  );

  const renderAccessibilityQuestion = () => (
    <FormField key="has_disability">
      <FieldLabel theme={theme}>Do you identify as having a physical disability?</FieldLabel>
      <RadioGroup>
        <RadioOption theme={theme}>
          <input
            type="radio"
            name="has_disability"
            value="no"
            checked={disabilityStatus !== 'yes'}
            onChange={() => handleDisabilityStatusChange('no')}
            disabled={isAdminPreview}
          />
          No
        </RadioOption>
        <RadioOption theme={theme}>
          <input
            type="radio"
            name="has_disability"
            value="yes"
            checked={disabilityStatus === 'yes'}
            onChange={() => handleDisabilityStatusChange('yes')}
            disabled={isAdminPreview}
          />
          Yes
        </RadioOption>
      </RadioGroup>
      {disabilityError && (
        <FieldError theme={theme}>{disabilityError}</FieldError>
      )}
    </FormField>
  );

  const renderDisabilityDetails = () => {
    if (disabilityStatus !== 'yes') {
      return null;
    }

    return (
      <FormField key="disability_types">
        <FieldLabel theme={theme}>Select the disability type(s) that apply to you</FieldLabel>
        <CheckboxGrid>
          {DISABILITY_OPTIONS.map(option => {
            const selected = disabilitySelections.includes(option);
            return (
              <CheckboxOption key={option} theme={theme} $selected={selected}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleDisabilityType(option)}
                  disabled={isAdminPreview}
                />
                <span>{option}</span>
              </CheckboxOption>
            );
          })}
        </CheckboxGrid>
        <HelperText theme={theme}>
          You can choose multiple options. Select “Other (please specify)” if none of the categories fit.
        </HelperText>
        {disabilitySelections.includes(OTHER_DISABILITY_OPTION) && (
          <div style={{ marginTop: '1rem' }}>
            <FieldLabel theme={theme}>Disability details</FieldLabel>
            <Input
              placeholder="Please specify"
              value={disabilityOther}
              onChange={(e) => handleDisabilityOtherChange(e.target.value)}
              disabled={isAdminPreview}
            />
            {disabilityOtherError && (
              <FieldError theme={theme}>{disabilityOtherError}</FieldError>
            )}
          </div>
        )}
      </FormField>
    );
  };

  const renderCvBlock = () => (
    <FormField key="cv_upload">
      <FieldLabel theme={theme}>Upload CV / Resume (optional)</FieldLabel>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={cvInputRef}
          type="file"
          name="cv"
          accept=".pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files && event.target.files[0];
            if (file) {
              handleFileChange('cv', file);
            }
          }}
          disabled={isAdminPreview}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            if (isAdminPreview) return;
            cvInputRef.current?.click();
          }}
          disabled={isAdminPreview}
        >
          {cvFile ? 'Change CV' : 'Upload CV'}
        </Button>
        {cvFile && !isAdminPreview && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleRemoveCv}
            style={{ color: theme.colors.error }}
          >
            Remove
          </Button>
        )}
      </div>
      {cvFile && (
        <SelectedFile theme={theme}>
          <span>{cvFile.name}</span>
          <span>{(cvFile.size / (1024 * 1024)).toFixed(2)} MB</span>
        </SelectedFile>
      )}
      <FileNote theme={theme}>
        Accepted formats: PDF, DOC, DOCX (maximum size 5 MB)
      </FileNote>
      {cvError && <FieldError theme={theme}>{cvError}</FieldError>}
    </FormField>
  );

  // ID Card upload block
  const idCardFile = formData.id_card instanceof File ? formData.id_card : null;
  const idCardError = fieldErrors.id_card;

  const renderIdCardBlock = () => {
    const fieldConfig = getFieldConfig('id_card');
    const label = fieldConfig?.field_label || 'Upload ID Card';
    const helpText = fieldConfig?.help_text || 'Accepted formats: PDF, JPG, PNG (maximum size 5 MB)';
    const isRequired = fieldConfig?.is_required !== false; // Default to required
    
    return (
    <FormField key="id_card_upload">
      <FieldLabel theme={theme}>
        {label} {isRequired && <span className="required">*</span>}
      </FieldLabel>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={idCardInputRef}
          type="file"
          name="id_card"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files && event.target.files[0];
            if (file) {
              handleFileChange('id_card', file);
            }
          }}
          disabled={isAdminPreview}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            if (isAdminPreview) return;
            idCardInputRef.current?.click();
          }}
          disabled={isAdminPreview}
        >
          {idCardFile ? 'Change ID Card' : 'Upload ID Card'}
        </Button>
        {idCardFile && !isAdminPreview && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setFormData(prev => ({ ...prev, id_card: null }));
              if (idCardInputRef.current) idCardInputRef.current.value = '';
            }}
            style={{ color: theme.colors.error }}
          >
            Remove
          </Button>
        )}
      </div>
      {idCardFile && (
        <SelectedFile theme={theme}>
          <span>{idCardFile.name}</span>
          <span>{(idCardFile.size / (1024 * 1024)).toFixed(2)} MB</span>
        </SelectedFile>
      )}
      <FileNote theme={theme}>
        {helpText}
      </FileNote>
      {idCardError && <FieldError theme={theme}>{idCardError}</FieldError>}
    </FormField>
  );
  };

  // Training options for checkbox
  const TRAINING_OPTIONS = [
    'Human Rights Advocacy',
    'Gender Equality & Women\'s Rights',
    'Legal Aid & Access to Justice',
    'Community Mobilization',
    'Leadership & Management',
    'Research & Documentation',
    'Monitoring & Evaluation',
    'Digital Security',
    'Financial Management',
    'Project Management',
    'Communication & Media',
    'Psychosocial Support',
    'Other'
  ];

  // Training history block
  const hasPreviousTraining = formData.has_previous_training === 'yes';
  const previousTrainings = Array.isArray(formData.previous_trainings) ? formData.previous_trainings : [];
  const interestedTrainings = Array.isArray(formData.interested_trainings) ? formData.interested_trainings : [];

  const addPreviousTraining = () => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: [
        ...(prev.previous_trainings || []),
        { name: '', institution: '', date: '', certificate: null }
      ]
    }));
  };

  const removePreviousTraining = (index) => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: prev.previous_trainings.filter((_, i) => i !== index)
    }));
  };

  const updatePreviousTraining = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      previous_trainings: prev.previous_trainings.map((training, i) => 
        i === index ? { ...training, [field]: value } : training
      )
    }));
  };

  const toggleInterestedTraining = (option) => {
    const current = Array.isArray(formData.interested_trainings) ? formData.interested_trainings : [];
    const exists = current.includes(option);
    const next = exists ? current.filter(t => t !== option) : [...current, option];
    setFormData(prev => ({ ...prev, interested_trainings: next }));
  };

  // Render interested trainings with options from form config
  const renderInterestedTrainingsBlock = (fieldConfig) => {
    const options = fieldConfig?.options?.length > 0 ? fieldConfig.options : TRAINING_OPTIONS;
    const label = fieldConfig?.field_label || 'Interested Training(s)';
    const helpText = fieldConfig?.help_text || 'Select all training programs you\'re interested in joining';
    const isRequired = fieldConfig?.is_required || false;

    return (
      <FormField key="interested_trainings">
        <FieldLabel theme={theme}>
          {label}
          {isRequired && <span className="required"> *</span>}
        </FieldLabel>
        <FieldHelp theme={theme} style={{ marginBottom: '0.75rem' }}>
          {helpText}
        </FieldHelp>
        <CheckboxGrid>
          {options.map((option, idx) => {
            const selected = interestedTrainings.includes(option);
            return (
              <CheckboxOption key={idx} theme={theme} $selected={selected}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleInterestedTraining(option)}
                  disabled={isAdminPreview}
                />
                {option}
              </CheckboxOption>
            );
          })}
        </CheckboxGrid>
        {fieldErrors.interested_trainings && (
          <FieldError theme={theme}>{fieldErrors.interested_trainings}</FieldError>
        )}
      </FormField>
    );
  };

  const renderTrainingBlock = () => {
    const fieldConfig = getFieldConfig('has_previous_training');
    const label = fieldConfig?.field_label || 'Have you previously taken any training?';
    
    return (
    <>
      <FormField key="has_previous_training">
        <FieldLabel theme={theme}>{label}</FieldLabel>
        <RadioGroup>
          <RadioOption theme={theme}>
            <input
              type="radio"
              name="has_previous_training"
              value="no"
              checked={!hasPreviousTraining}
              onChange={() => {
                setFormData(prev => ({
                  ...prev,
                  has_previous_training: 'no',
                  previous_trainings: [],
                }));
              }}
              disabled={isAdminPreview}
            />
            No
          </RadioOption>
          <RadioOption theme={theme}>
            <input
              type="radio"
              name="has_previous_training"
              value="yes"
              checked={hasPreviousTraining}
              onChange={() => {
                setFormData(prev => ({
                  ...prev,
                  has_previous_training: 'yes',
                  previous_trainings: prev.previous_trainings?.length ? prev.previous_trainings : [{ name: '', institution: '', date: '', certificate: null }]
                }));
              }}
              disabled={isAdminPreview}
            />
            Yes
          </RadioOption>
        </RadioGroup>
      </FormField>

      {hasPreviousTraining && (
        <div style={{ 
          marginLeft: '1.5rem', 
          paddingLeft: '1.5rem', 
          borderLeft: `3px solid ${theme.colors.primary}40` 
        }}>
          {previousTrainings.map((training, index) => (
            <FormField key={`previous_training_${index}`} style={{ 
              marginBottom: '1.5rem',
              padding: '1rem',
              background: theme.colors.background.secondary,
              borderRadius: '8px',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <FieldLabel theme={theme} style={{ margin: 0 }}>
                  Training #{index + 1}
                </FieldLabel>
                {previousTrainings.length > 1 && !isAdminPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removePreviousTraining(index)}
                    style={{ color: theme.colors.error, padding: '0.25rem 0.5rem' }}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <FieldLabel theme={theme}>
                Training Name <span className="required">*</span>
              </FieldLabel>
              <Input
                type="text"
                value={training.name || ''}
                onChange={(e) => updatePreviousTraining(index, 'name', e.target.value)}
                placeholder="Name of the training program"
                disabled={isAdminPreview}
              />
              {fieldErrors[`previous_training_${index}_name`] && (
                <FieldError theme={theme}>{fieldErrors[`previous_training_${index}_name`]}</FieldError>
              )}

              <div style={{ marginTop: '1rem' }}>
                <FieldLabel theme={theme}>
                  Trainer / Institution Name <span className="required">*</span>
                </FieldLabel>
                <Input
                  type="text"
                  value={training.institution || ''}
                  onChange={(e) => updatePreviousTraining(index, 'institution', e.target.value)}
                  placeholder="Name of trainer or institution"
                  disabled={isAdminPreview}
                />
                {fieldErrors[`previous_training_${index}_institution`] && (
                  <FieldError theme={theme}>{fieldErrors[`previous_training_${index}_institution`]}</FieldError>
                )}
              </div>

              <div style={{ marginTop: '1rem' }}>
                <FieldLabel theme={theme}>Training Date</FieldLabel>
                <Input
                  type="date"
                  value={training.date || ''}
                  onChange={(e) => updatePreviousTraining(index, 'date', e.target.value)}
                  disabled={isAdminPreview}
                />
              </div>

              <div style={{ marginTop: '1rem' }}>
                <FieldLabel theme={theme}>Training Certificate (optional)</FieldLabel>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) updatePreviousTraining(index, 'certificate', file);
                  }}
                  disabled={isAdminPreview}
                  style={{ marginTop: '0.5rem' }}
                />
                {training.certificate instanceof File && (
                  <SelectedFile theme={theme}>
                    <span>{training.certificate.name}</span>
                    <span>{(training.certificate.size / (1024 * 1024)).toFixed(2)} MB</span>
                  </SelectedFile>
                )}
                <FileNote theme={theme}>
                  Accepted formats: PDF, JPG, PNG (maximum size 5 MB)
                </FileNote>
              </div>
            </FormField>
          ))}

          {!isAdminPreview && (
            <Button
              type="button"
              variant="secondary"
              onClick={addPreviousTraining}
              style={{ marginTop: '0.5rem' }}
            >
              + Add Another Training
            </Button>
          )}
        </div>
      )}
    </>
  );
  };

  function renderField(field) {
    const fieldName = field.field_name;
    const value = formData[fieldName] || '';
    const isRegionField = REGION_FIELD_NAMES.has(fieldName);
    const baseType = isRegionField ? 'select' : field.field_type;
    const options = isRegionField ? ETHIOPIAN_REGIONS : field.options;
    const isRequired = field.is_required;
    const errorMessage = fieldErrors[fieldName];
    const inputStyle = errorMessage ? {
      borderColor: theme.colors.error,
      boxShadow: `0 0 0 2px ${theme.colors.error}1f`
    } : undefined;

    switch (baseType) {
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            theme={theme}
            style={inputStyle}
            aria-invalid={!!errorMessage}
          />
        );

      case 'select':
        return (
          <Select
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            required={isRequired}
            theme={theme}
            style={inputStyle}
            aria-invalid={!!errorMessage}
          >
            <option value="">Select an option...</option>
            {Array.isArray(options) && options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup>
            {Array.isArray(options) && options.map((option, idx) => (
              <RadioOption key={idx} theme={theme}>
                <input
                  type="radio"
                  name={fieldName}
                  value={option}
                  checked={value === option}
                  onChange={() => handleChange(fieldName, option)}
                  required={isRequired}
                />
                {option}
              </RadioOption>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <CheckboxGrid>
            {Array.isArray(options) && options.map((option, idx) => {
              const selectedValues = Array.isArray(formData[fieldName]) ? formData[fieldName] : [];
              const selected = selectedValues.includes(option);
              return (
                <CheckboxOption key={idx} theme={theme} $selected={selected}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      const currentValues = Array.isArray(formData[fieldName]) ? formData[fieldName] : [];
                      const nextValues = selected
                        ? currentValues.filter(item => item !== option)
                        : [...currentValues, option];
                      handleChange(fieldName, nextValues);
                    }}
                  />
                  {option}
                </CheckboxOption>
              );
            })}
          </CheckboxGrid>
        );

      case 'file':
        return (
          <Input
            type="file"
            onChange={(e) => handleFileChange(fieldName, e.target.files?.[0] || null)}
            required={isRequired}
            theme={theme}
            style={inputStyle}
            aria-invalid={!!errorMessage}
          />
        );

      default:
        return (
          <Input
            type={baseType || 'text'}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            theme={theme}
            style={inputStyle}
            aria-invalid={!!errorMessage}
          />
        );
    }
  }

  const sortedFields = useMemo(() => {
    return activeFields
      .map((field, index) => ({
        field,
        order: field.display_order ?? index,
      }))
      .sort((a, b) => a.order - b.order)
      .map(item => item.field);
  }, [activeFields]);

  // Check if field exists in form configuration
  const hasField = (fieldName) => {
    return sortedFields.some(f => f.field_name === fieldName);
  };

  // Get field config from form
  const getFieldConfig = (fieldName) => {
    return sortedFields.find(f => f.field_name === fieldName);
  };

  const renderFormFields = () => {
    const elements = [];
    let accessibilityQuestionRendered = false;
    let cvRendered = false;
    let idCardRendered = false;
    let interestedTrainingsRendered = false;
    let previousTrainingRendered = false;

    sortedFields.forEach(field => {
      const name = field.field_name;
      if (name === 'has_disability') {
        elements.push(renderAccessibilityQuestion());
        accessibilityQuestionRendered = true;
        const details = renderDisabilityDetails();
        if (details) {
          elements.push(details);
        }
      } else if (name === 'disability_types' || name === 'disability_other') {
        // handled within the accessibility block
      } else if (name === 'cv') {
        elements.push(renderCvBlock());
        cvRendered = true;
      } else if (name === 'id_card') {
        // Render ID card field from form config
        elements.push(renderIdCardBlock());
        idCardRendered = true;
      } else if (name === 'interested_trainings') {
        // Render interested trainings from form config (use options from field if available)
        const fieldConfig = getFieldConfig('interested_trainings');
        elements.push(renderInterestedTrainingsBlock(fieldConfig));
        interestedTrainingsRendered = true;
      } else if (name === 'has_previous_training') {
        // Render previous training section
        elements.push(renderTrainingBlock());
        previousTrainingRendered = true;
      } else {
        elements.push(renderDynamicField(field, name));
      }
    });

    // Only add default fields if they weren't in the form config
    if (!accessibilityQuestionRendered) {
      elements.unshift(renderAccessibilityQuestion());
      const details = renderDisabilityDetails();
      if (details) {
        elements.splice(1, 0, details);
      }
    }

    if (!cvRendered) {
      elements.push(renderCvBlock());
    }

    // Only add new membership fields if they exist in form config but weren't rendered
    // (backwards compatibility - don't add if not configured)

    return elements;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});

    try {
      // Ensure all form fields are included, even if empty
      const submitData = {
        form_id: form.id,
      };
      
      // Handle both camelCase and snake_case
      const activeFields = form.activeFields || form.active_fields || [];
      
      // Add all form fields explicitly
      activeFields.forEach(field => {
        const fieldName = field.field_name;
        const value = formData[fieldName];
        
        if (field.field_type === 'file') {
          // For file fields, only include if a file was actually selected
          if (value instanceof File) {
            submitData[fieldName] = value;
          }
          // Don't include file fields if no file was selected
        } else if (value !== undefined) {
          submitData[fieldName] = value;
        } else if (field.field_type === 'checkbox') {
          // Initialize checkbox as empty array if not set
          submitData[fieldName] = [];
        } else {
          // For other fields, send empty string if not set
          submitData[fieldName] = '';
        }
      });

      // Check for required file fields that are missing
      const requiredFileFields = activeFields
        .filter(field => field.field_type === 'file' && field.is_required)
        .map(field => field.field_name);
      
      const missingFileFields = requiredFileFields.filter(fieldName => {
        const value = submitData[fieldName];
        return !(value instanceof File);
      });
      
      if (missingFileFields.length > 0) {
        const missingFields = activeFields
          .filter(field => missingFileFields.includes(field.field_name))
          .map(field => field.field_label || field.field_name)
          .join(', ');
        const newErrors = {};
        missingFileFields.forEach(fieldName => {
          newErrors[fieldName] = 'This file is required.';
        });
        setFieldErrors(newErrors);
        toast.error(`Please select files for the following required fields: ${missingFields}`);
        setSubmitting(false);
        return;
      }

      if (disabilityStatus === 'yes' && disabilitySelections.length === 0) {
        setFieldErrors(prev => ({
          ...prev,
          disability_types: 'Please select at least one disability type.',
        }));
        toast.error('Please select at least one disability type.');
        setSubmitting(false);
        return;
      }

      if (
        disabilityStatus === 'yes' &&
        disabilitySelections.includes(OTHER_DISABILITY_OPTION) &&
        !disabilityOther.trim()
      ) {
        setFieldErrors(prev => ({
          ...prev,
          disability_other: 'Please provide additional details.',
        }));
        toast.error('Please describe the disability details.');
        setSubmitting(false);
        return;
      }

      submitData.has_disability = disabilityStatus;
      submitData.disability_types = disabilityStatus === 'yes'
        ? disabilitySelections.filter(Boolean)
        : [];
      submitData.disability_other =
        disabilityStatus === 'yes' && disabilitySelections.includes(OTHER_DISABILITY_OPTION)
          ? disabilityOther.trim()
          : '';

      if (formData.cv instanceof File) {
        submitData.cv = formData.cv;
      }

      // Add new fields for direct membership
      const idCardField = activeFields.find(f => f.field_name === 'id_card');
      if (formData.id_card instanceof File) {
        submitData.id_card = formData.id_card;
      } else if (idCardField && idCardField.is_required !== false) {
        // ID card is required only if field exists and is marked as required
        setFieldErrors(prev => ({
          ...prev,
          id_card: 'ID card is required.',
        }));
        toast.error('Please upload your ID card.');
        setSubmitting(false);
        return;
      }

      // Interested trainings (multiple selection)
      submitData.interested_trainings = JSON.stringify(formData.interested_trainings || []);
      submitData.has_previous_training = formData.has_previous_training || 'no';

      // Previous trainings (multiple entries)
      if (formData.has_previous_training === 'yes') {
        const trainings = formData.previous_trainings || [];
        const errors = {};
        let hasError = false;

        trainings.forEach((training, index) => {
          if (!training.name?.trim()) {
            errors[`previous_training_${index}_name`] = 'Training name is required.';
            hasError = true;
          }
          if (!training.institution?.trim()) {
            errors[`previous_training_${index}_institution`] = 'Trainer/Institution name is required.';
            hasError = true;
          }
        });

        if (hasError) {
          setFieldErrors(prev => ({ ...prev, ...errors }));
          toast.error('Please fill in all required training fields.');
          setSubmitting(false);
          return;
        }

        // Serialize trainings data (without files)
        const trainingsData = trainings.map(t => ({
          name: t.name,
          institution: t.institution,
          date: t.date || ''
        }));
        submitData.previous_trainings = JSON.stringify(trainingsData);

        // Add certificate files separately
        trainings.forEach((training, index) => {
          if (training.certificate instanceof File) {
            submitData[`training_certificate_${index}`] = training.certificate;
          }
        });
      }

      console.log('🚀 SUBMITTING APPLICATION:', {
        form_id: form.id,
        formData_keys: Object.keys(formData),
        submitData_keys: Object.keys(submitData),
        fileFields: Object.keys(submitData).filter(key => submitData[key] instanceof File).map(key => ({
          field: key,
          fileName: submitData[key].name,
          fileSize: submitData[key].size
        })),
        requiredFileFields: requiredFileFields,
        missingFileFields: missingFileFields,
        submitData_sample: Object.keys(submitData).reduce((acc, key) => {
          const value = submitData[key];
          if (value instanceof File) {
            acc[key] = `[File: ${value.name}, ${value.size} bytes]`;
          } else if (Array.isArray(value)) {
            acc[key] = `[Array: ${value.length} items]`;
          } else {
            acc[key] = typeof value === 'string' ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : value;
          }
          return acc;
        }, {})
      });

      const response = await membershipApplicationAPI.submitApplication(submitData);
      const resultData = response.data?.data || response.data;
      if (resultData) {
        setSubmissionResult(resultData);
      }
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 403) {
        // Form is closed
        toast.error(error.response?.data?.message || 'Application is closed');
      } else if (error.response?.status === 422) {
        // Validation errors
        const errors = error.response?.data?.errors || {};
        const normalizedErrors = {};
        
        // Get field labels for better error messages
        const activeFields = form.activeFields || form.active_fields || [];
        const fieldMap = {};
        activeFields.forEach(field => {
          fieldMap[field.field_name] = field.field_label || field.field_name;
        });
        
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => {
            const fieldLabel = fieldMap[field] || field;
            const message = Array.isArray(messages) ? messages[0] : messages;
            normalizedErrors[field] = message;
            return `${fieldLabel}: ${message}`;
          })
          .join('\n');
        
        setFieldErrors(normalizedErrors);
        toast.error(`Validation failed:\n${errorMessages}`, {
          duration: 5000,
        });
        
        // Also log to console for debugging
        console.error('Validation errors:', errors);
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to submit application';
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content>
          <div>Loading...</div>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  if (!form) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content>
          <ApplicationCard theme={theme}>
            <Title theme={theme}>Form Not Found</Title>
            <Description theme={theme}>
              The membership application form you're looking for doesn't exist or has been removed.
            </Description>
          </ApplicationCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  // Check if form is open
  const isOpen = (() => {
    if (!form.is_active) return false;
    const now = new Date();
    if (form.opens_at) {
      const opensAt = new Date(form.opens_at);
      if (opensAt > now) return false;
    }
    if (form.closes_at) {
      const closesAt = new Date(form.closes_at);
      if (closesAt < now) return false;
    }
    return true;
  })();

  if (!isOpen || form.status === 'closed') {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content>
          <ApplicationCard theme={theme}>
            <ClosedMessage>
              <ClosedIcon theme={theme}>
                <Lock size={40} />
              </ClosedIcon>
              <Title theme={theme}>Application Closed</Title>
              <Description theme={theme}>
                {form.closed_message || 'The membership application period is currently closed.'}
              </Description>
            </ClosedMessage>
          </ApplicationCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container theme={theme}>
        <PublicHeader theme={theme} />
        <Content>
          <ApplicationCard theme={theme}>
            <ClosedMessage>
              <ClosedIcon theme={theme} style={{ background: theme.colors.success + '20', color: theme.colors.success }}>
                <CheckCircle size={40} />
              </ClosedIcon>
              <Title theme={theme}>Application Submitted!</Title>
              <Description theme={theme}>
                Thank you for your application. Your tracking number is below—please keep it safe so you can check your application status at any time.
              </Description>
              {submissionResult?.registration_no && (
                <TrackingBox theme={theme}>
                  <div style={{ textTransform: 'uppercase', fontSize: '0.75rem', color: theme.colors.text.secondary, letterSpacing: '0.08em' }}>
                    Registration Number
                  </div>
                  <TrackingNumber theme={theme}>{submissionResult.registration_no}</TrackingNumber>
                  <TrackingActions>
                    <Button
                      variant="primary"
                      onClick={() => {
                        navigator.clipboard.writeText(submissionResult.registration_no);
                        toast.success('Tracking number copied to clipboard');
                      }}
                      leftIcon={<Copy size={16} />}
                    >
                      Copy Tracking Number
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleDownloadTrackingPdf}
                      leftIcon={<Download size={16} />}
                    >
                      Download PDF
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        const printWindow = window.open('', '', 'width=600,height=400');
                        if (!printWindow) {
                          toast.error('Unable to open print window');
                          return;
                        }
                        const timestamp = new Date().toLocaleString();
                        printWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                            <head>
                              <title>Registration Number</title>
                              <style>
                                body { font-family: Arial, sans-serif; padding: 40px; color: #1f2937; }
                                h1 { font-size: 22px; margin-bottom: 16px; }
                                .number {
                                  border: 2px dashed #2563eb;
                                  padding: 20px;
                                  border-radius: 12px;
                                  font-size: 24px;
                                  letter-spacing: 0.15em;
                                  text-align: center;
                                  margin-bottom: 24px;
                                  color: #1d4ed8;
                                }
                                .meta { font-size: 14px; color: #4b5563; }
                              </style>
                            </head>
                            <body>
                              <h1>Ethiopia WHRDN Membership Registration</h1>
                              <div class="number">${submissionResult.registration_no}</div>
                              <div class="meta">Generated: ${timestamp}</div>
                              <p>Please keep this registration number for your records. You will need it to check your application status.</p>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.focus();
                        printWindow.print();
                        printWindow.close();
                      }}
                      leftIcon={<Printer size={16} />}
                    >
                      Print Number
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => window.location.href = '/application-status'}
                      leftIcon={<ExternalLink size={16} />}
                    >
                      Check Application Status
                    </Button>
                  </TrackingActions>
                </TrackingBox>
              )}
              <p style={{ marginTop: '1.5rem', color: theme.colors.text.secondary }}>
                You will also receive updates via the contact information you provided.
              </p>
              <Button
                variant="primary"
                onClick={() => window.location.href = '/'}
                style={{ marginTop: '1rem' }}
              >
                Return to Home
              </Button>
            </ClosedMessage>
          </ApplicationCard>
        </Content>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  return (
    <Container theme={theme}>
      <PublicHeader theme={theme} />
      <Content>
        <ApplicationCard theme={theme}>
          <Title theme={theme}>{form.title}</Title>
          {form.description && (
            <Description theme={theme}>{form.description}</Description>
          )}
          
          {form.instructions && (
            <Instructions theme={theme}>
              <strong>Instructions:</strong>
              <div style={{ marginTop: '0.5rem' }}>{form.instructions}</div>
            </Instructions>
          )}

          <form onSubmit={handleSubmit}>
            {renderFormFields()}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </ApplicationCard>
      </Content>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default PublicMembershipApplication;

